import {
  type AgentDto,
  type ChannelDto,
  type ConversationDto,
  can,
  type RunStatus,
  TERMINAL_RUN_STATUSES,
} from '@af/shared';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentHoverCard } from '@/components/dashboard/AgentHoverCard';
import { activityText } from '@/components/dashboard/LiveOverview';
import { ManageChannelDialog } from '@/components/talk/ManageChannelDialog';
import { AgentOrb } from '@/components/ui/agent-orb';
import {
  IconBuilding,
  IconCheck,
  IconChevron,
  IconPlay,
  IconPlus,
  IconSearch,
  IconSettings,
  IconSparkle,
  IconUsers,
} from '@/components/ui/icons';
import { StatusDot } from '@/components/ui/status-dot';
import { usePaneActions } from '@/components/workspace/actions';
import { BroadcastDialog } from '@/components/workspace/BroadcastDialog';
import { keepUnread } from '@/lib/conversation-read';
import { relTime } from '@/lib/format';
import { type AgentLive, isWaitingRun, useAgentsLive } from '@/lib/live-agents';
import { useRole } from '@/lib/permissions';
import { useAgents, useApiMutation, useChannelSearch, useChannels, useConversations } from '@/lib/queries';
import { isOnline, isTemporaryChannel, sortByPresence, splitChannels } from '@/lib/talk';
import { cn, safeStorage } from '@/lib/utils';

const COLLAPSE_KEY = 'af:talk-collapsed';
const RECENT_CHATS = 8;

const ROW =
  'flex h-8 touch:h-10 w-full items-center gap-2 rounded-lg px-2 text-sm text-fg-2 transition-colors hover:bg-hover hover:text-fg';
const ACTIVE = 'bg-hover font-medium text-fg';

/** Presence dot under an avatar: working (coral pulse), waiting for you, available, paused. */
function Presence({ status, className }: { status: AgentLive['status']; className?: string }) {
  const { t } = useTranslation();
  const tone =
    status === 'running'
      ? 'running'
      : status === 'approval'
        ? 'approval'
        : status === 'paused'
          ? 'paused'
          : 'done';
  return (
    <StatusDot
      tone={tone}
      label={t(`talk.presence.${status}`)}
      className={cn('absolute -right-0.5 -bottom-0.5 ring-2 ring-panel', className)}
    />
  );
}

function Section({
  id,
  title,
  count,
  action,
  children,
}: {
  id: string;
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(() => !!safeStorage.get<string[]>(COLLAPSE_KEY)?.includes(id));
  const toggle = () =>
    setCollapsed((c) => {
      const all = new Set(safeStorage.get<string[]>(COLLAPSE_KEY) ?? []);
      if (c) all.delete(id);
      else all.add(id);
      safeStorage.set(COLLAPSE_KEY, [...all]);
      return !c;
    });
  return (
    <section className="mb-2">
      <div className="group flex items-center gap-1 px-1">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          className="flex h-7 min-w-0 flex-1 items-center gap-1 rounded-md px-1 text-2xs font-medium tracking-wide text-faint uppercase hover:text-fg"
        >
          <IconChevron size={11} className={cn('transition-transform', !collapsed && 'rotate-90')} />
          <span className="truncate">{title}</span>
          {count ? <span className="text-faint normal-case">· {count}</span> : null}
        </button>
        {action}
      </div>
      {collapsed ? null : <ul className="mt-0.5">{children}</ul>}
    </section>
  );
}

function Badge({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="ml-auto min-w-5 shrink-0 rounded-full bg-accent px-1.5 text-center text-2xs leading-5 font-semibold text-white">
      {n > 99 ? '99+' : n}
    </span>
  );
}

/**
 * Left column of the chat (Slack-like): the Company chat, channels, your DMs with agents (everyone, with
 * presence) and the DMs agents have among themselves (read along). Replaces the old conversation list.
 */
export function TalkSidebar({ className }: { className?: string }) {
  const { t } = useTranslation();
  const role = useRole();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search }) as { new?: boolean; agent?: string };
  // `/chat` without a new chat open is the company overview.
  const overview = pathname === '/chat' && !search.new && !search.agent;
  const { newChat } = usePaneActions();
  const agentsQ = useAgents();
  const channelsQ = useChannels();
  const conversations = useConversations();
  const live = useAgentsLive({ tails: true });
  const [q, setQ] = useState('');
  const [manage, setManage] = useState(false);
  const [broadcast, setBroadcast] = useState(false);
  const [allChats, setAllChats] = useState(false);
  const hits = useChannelSearch(q);
  const canSend = can(role, 'conversation:create') && can(role, 'agent:run');
  const canManage = can(role, 'agent:update');
  const canCreateAgent = can(role, 'agent:create');

  const byAgent = useMemo(() => new Map((agentsQ.data ?? []).map((a) => [a.id, a])), [agentsQ.data]);
  const liveById = useMemo(() => new Map(live.list.map((l) => [l.agent.id, l])), [live.list]);
  const { channels, dms } = splitChannels(channelsQ.data ?? []);
  const people = sortByPresence(live.list.filter((l) => l.agent.kind === 'persistent'));
  const online = people.filter((p) => isOnline(p.status)).length;
  // Latest conversation per agent: a click on a person continues it (Slack: one DM per person).
  const unreadByAgent = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of conversations.data ?? []) m.set(c.agentId, (m.get(c.agentId) ?? 0) + (c.unread ?? 0));
    return m;
  }, [conversations.data]);
  const setRead = useApiMutation('setConversationRead', { invalidate: [['conversations']] });
  const toggleRead = (c: ConversationDto) => {
    const read = (c.unread ?? 0) > 0;
    keepUnread(c.id, !read);
    setRead.mutate({ params: { id: c.id }, body: { read } });
  };
  const latestByAgent = useMemo(() => {
    const m = new Map<string, ConversationDto>();
    for (const c of conversations.data ?? []) {
      const prev = m.get(c.agentId);
      if (!prev || prev.updatedAt < c.updatedAt) m.set(c.agentId, c);
    }
    return m;
  }, [conversations.data]);
  const activeConv = useMemo(() => {
    const m = new Set<string>();
    for (const l of live.list)
      if (l.run?.conversationId && !TERMINAL_RUN_STATUSES.has(l.run.status as RunStatus))
        m.add(l.run.conversationId);
    return m;
  }, [live.list]);

  const needle = q.trim().toLowerCase();
  const match = (s: string) => !needle || s.toLowerCase().includes(needle);
  // A person who is answering in a chat right now opens that chat; otherwise the latest one (or a new one).
  const openAgent = (a: AgentDto, l: AgentLive) => {
    const working =
      l.run && !TERMINAL_RUN_STATUSES.has(l.run.status as RunStatus) ? l.run.conversationId : null;
    const c = working ?? latestByAgent.get(a.id)?.id;
    if (c) void navigate({ to: '/chat/$conversationId', params: { conversationId: c } });
    else newChat(a.id);
  };
  const recent = [...(conversations.data ?? [])]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .filter((c) => match(`${c.title} ${byAgent.get(c.agentId)?.name ?? ''}`));

  const channelRow = (c: ChannelDto) => {
    const to = `/chat/c/${c.id}`;
    const active = pathname === to;
    const working = c.activeAgentIds.length > 0;
    const pair = c.kind === 'dm' ? c.members.map((m) => byAgent.get(m.agentId)).filter(Boolean) : [];
    return (
      <li key={c.id}>
        <Link
          to="/chat/c/$channelId"
          params={{ channelId: c.id }}
          aria-current={active ? 'page' : undefined}
          className={cn(ROW, active && ACTIVE, c.unread > 0 && 'font-semibold text-fg')}
        >
          {c.kind === 'dm' ? (
            <span className="relative flex w-6 shrink-0">
              {pair.slice(0, 2).map((a, i) => (
                <AgentOrb
                  key={a!.id}
                  seed={a!.id}
                  size={16}
                  avatar={a!.avatar}
                  name={a!.name}
                  className={cn(i === 1 && '-ml-2 ring-2 ring-panel')}
                />
              ))}
            </span>
          ) : (
            <span aria-hidden className="w-4 shrink-0 text-center text-faint">
              #
            </span>
          )}
          <span className="min-w-0 truncate">{c.kind === 'dm' ? c.name : c.slug}</span>
          {isTemporaryChannel(c) ? (
            <span className="shrink-0 rounded bg-panel-2 px-1 text-2xs text-faint">
              {t('talk.temporary')}
            </span>
          ) : null}
          {working ? <StatusDot tone="running" label={t('talk.replying')} className="ml-1 shrink-0" /> : null}
          <Badge n={c.unread} />
        </Link>
      </li>
    );
  };

  const personRow = (l: AgentLive) => {
    const a = l.agent;
    const conv = latestByAgent.get(a.id);
    const active = !!conv && pathname === `/chat/${conv.id}`;
    // What it is doing right now, also for work outside a chat (tasks); "typing" only while it writes.
    const now =
      l.run && (l.status === 'running' || l.status === 'approval')
        ? l.activity?.kind === 'writing' && !isWaitingRun(l.run)
          ? t('talk.typing')
          : activityText(t, l)
        : null;
    return (
      <li key={a.id} className="flex items-center gap-1">
        <AgentHoverCard live={l}>
          <button
            type="button"
            onClick={() => openAgent(a, l)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              ROW,
              'min-w-0 flex-1',
              now && 'h-auto py-1',
              active && ACTIVE,
              l.status === 'paused' && 'opacity-60',
            )}
          >
            <span className="relative shrink-0">
              <AgentOrb seed={a.id} size={20} avatar={a.avatar} name={a.name} />
              <Presence status={l.status} />
            </span>
            <span className="flex min-w-0 flex-col items-start text-left">
              <span className="flex min-w-0 max-w-full items-baseline gap-1.5">
                <span className="min-w-0 truncate">{a.name}</span>
                <span className="min-w-0 truncate text-xs font-normal text-faint">{a.roleTitle}</span>
              </span>
              {now ? (
                <span
                  className={cn(
                    'max-w-full truncate text-2xs font-normal',
                    isWaitingRun(l.run) ? 'text-muted' : 'text-claude',
                  )}
                >
                  {now}
                </span>
              ) : null}
            </span>
            <Badge n={unreadByAgent.get(a.id) ?? 0} />
          </button>
        </AgentHoverCard>
        {now && l.run ? (
          <Link
            to="/runs/$id"
            params={{ id: l.run.id }}
            aria-label={t('hoverCard.activeRun', { name: a.name })}
            title={t('hoverCard.activeRun', { name: a.name })}
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-claude hover:bg-hover touch:size-10"
          >
            <IconPlay size={14} />
          </Link>
        ) : null}
        <Link
          to="/agents/$id"
          params={{ id: a.id }}
          search={{ tab: 'config' }}
          aria-label={t('inspector.agentSettings', { name: a.name })}
          title={t('inspector.agentSettings', { name: a.name })}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted hover:bg-hover hover:text-fg touch:size-10"
        >
          <IconSettings size={14} />
        </Link>
      </li>
    );
  };

  return (
    <nav
      aria-label={t('chats.title')}
      className={cn(
        'flex min-h-0 w-full flex-col gap-2 rounded-2xl border border-border bg-panel p-2.5 lg:w-[264px] lg:shrink-0',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 px-1 pt-0.5">
        <h2 className="text-[15px] font-semibold">{t('nav.company')}</h2>
        {canSend ? (
          <button
            type="button"
            onClick={() => newChat()}
            aria-label={t('chats.new')}
            title={t('chats.new')}
            className="inline-flex size-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-hover hover:text-fg"
          >
            <IconPlus size={14} />
          </button>
        ) : null}
      </div>

      <label className="relative block">
        <IconSearch
          size={14}
          className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-faint"
        />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('talk.search')}
          aria-label={t('talk.search')}
          className="h-8 w-full rounded-lg border border-border bg-bg pr-3 pl-8 text-sm touch:h-10 touch:text-[16px] placeholder:text-faint focus:border-accent focus:outline-none"
        />
      </label>

      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
        <Link
          to="/chat"
          aria-current={overview ? 'page' : undefined}
          className={cn(ROW, 'h-9', overview && ACTIVE)}
        >
          <span className="flex size-5 shrink-0 items-center justify-center rounded-md bg-panel-2 text-muted">
            <IconBuilding size={12} />
          </span>
          <span className="min-w-0 truncate">{t('talk.overview')}</span>
        </Link>
        <Link
          to="/master"
          aria-current={pathname === '/master' ? 'page' : undefined}
          className={cn(ROW, 'mb-2 h-9', pathname === '/master' && ACTIVE)}
        >
          <span className="flex size-5 shrink-0 items-center justify-center rounded-md bg-accent text-white">
            <IconSparkle size={12} />
          </span>
          <span className="min-w-0 truncate">{t('chats.company')}</span>
        </Link>

        {needle.length >= 2 && (hits.data?.length ?? 0) > 0 ? (
          <Section id="hits" title={t('talk.messages')} count={hits.data!.length}>
            {hits.data!.slice(0, 8).map((h) => (
              <li key={h.id}>
                <Link
                  to="/chat/c/$channelId"
                  params={{ channelId: h.channelId }}
                  search={h.replyToId ? { thread: h.replyToId } : {}}
                  className="block rounded-lg px-2 py-1.5 hover:bg-hover"
                >
                  <span className="block truncate text-xs text-muted">
                    {h.channelKind === 'dm' ? h.channelName : `#${h.channelName}`} · {h.authorName}
                  </span>
                  <span className="line-clamp-2 text-sm text-fg-2">{h.body}</span>
                </Link>
              </li>
            ))}
          </Section>
        ) : null}

        <Section
          id="channels"
          title={t('talk.channels')}
          action={
            canManage ? (
              <button
                type="button"
                onClick={() => setManage(true)}
                aria-label={t('channels.new')}
                title={t('channels.new')}
                className="inline-flex size-6 items-center justify-center rounded-md text-faint hover:bg-hover hover:text-fg"
              >
                <IconPlus size={12} />
              </button>
            ) : null
          }
        >
          {channels.filter((c) => match(`${c.slug} ${c.name} ${c.topic}`)).map(channelRow)}
          {channels.length === 0 && !channelsQ.isPending ? (
            <li className="px-2 py-1 text-xs text-faint">{t('talk.noChannels')}</li>
          ) : null}
        </Section>

        <Section
          id="people"
          title={t('talk.people')}
          count={online}
          action={
            canCreateAgent ? (
              <Link
                to="/agents/new"
                aria-label={t('nav.newAgent')}
                title={t('nav.newAgent')}
                className="inline-flex size-6 items-center justify-center rounded-md text-faint hover:bg-hover hover:text-fg"
              >
                <IconPlus size={12} />
              </Link>
            ) : null
          }
        >
          {people.filter((p) => match(`${p.agent.name} ${p.agent.roleTitle ?? ''}`)).map(personRow)}
        </Section>

        {dms.length ? (
          <Section id="agent-dms" title={t('talk.agentDms')} count={dms.length}>
            {dms.filter((c) => match(c.name)).map(channelRow)}
          </Section>
        ) : null}

        {recent.length ? (
          <Section id="chats" title={t('talk.recentChats')}>
            {(allChats || needle ? recent : recent.slice(0, RECENT_CHATS)).map((c) => {
              const a = byAgent.get(c.agentId);
              const active = pathname === `/chat/${c.id}`;
              const unread = c.unread ?? 0;
              return (
                <li key={c.id} className="group relative">
                  <Link
                    to="/chat/$conversationId"
                    params={{ conversationId: c.id }}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      ROW,
                      'h-auto py-1.5',
                      active && ACTIVE,
                      unread > 0 && 'font-semibold text-fg',
                    )}
                  >
                    <span className="relative shrink-0">
                      <AgentOrb seed={c.agentId} size={20} avatar={a?.avatar} name={a?.name} />
                      {activeConv.has(c.id) && liveById.get(c.agentId) ? (
                        <Presence status={liveById.get(c.agentId)!.status} />
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{c.title}</span>
                      <span className="block truncate text-2xs font-normal text-faint">
                        {a?.name ?? t('chats.unknownAgent')} · {relTime(c.updatedAt)}
                      </span>
                    </span>
                    <Badge n={unread} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => toggleRead(c)}
                    aria-label={unread ? t('talk.markRead') : t('talk.markUnread')}
                    title={unread ? t('talk.markRead') : t('talk.markUnread')}
                    className="absolute top-1/2 right-1.5 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-md border border-border bg-panel text-muted opacity-0 hover:text-fg focus-visible:opacity-100 group-hover:opacity-100"
                  >
                    {unread ? (
                      <IconCheck size={13} />
                    ) : (
                      <span aria-hidden className="size-2 rounded-full bg-accent" />
                    )}
                  </button>
                </li>
              );
            })}
            {!needle && recent.length > RECENT_CHATS ? (
              <li>
                <button
                  type="button"
                  className="px-2 py-1 text-xs text-muted hover:text-fg"
                  onClick={() => setAllChats((v) => !v)}
                >
                  {allChats ? t('chats.showFewer') : t('chats.showAll', { count: recent.length })}
                </button>
              </li>
            ) : null}
          </Section>
        ) : null}
      </div>

      {canSend ? (
        <>
          <button
            type="button"
            onClick={() => setBroadcast(true)}
            className="flex h-9 touch:h-11 items-center gap-2 rounded-lg px-2 text-sm text-muted transition-colors hover:bg-hover hover:text-fg"
          >
            <IconUsers size={14} /> {t('broadcast.open')}
          </button>
          <BroadcastDialog open={broadcast} onOpenChange={setBroadcast} />
        </>
      ) : null}
      {manage ? (
        <ManageChannelDialog
          channel={null}
          agents={(agentsQ.data ?? []).filter((a) => a.status !== 'archived' && a.kind === 'persistent')}
          onClose={(created) => {
            setManage(false);
            if (created) void navigate({ to: '/chat/c/$channelId', params: { channelId: created.id } });
          }}
        />
      ) : null}
    </nav>
  );
}
